import * as React from 'react'

import { classList } from 'react-classlist-helper'

import { IAttachment } from '~/client/graph'

import { PhotoIcon } from '../icons/PhotoIcon'
import { getFileKind } from './fileKind'
import FileTypeIcon from './FileTypeIcon'

interface AttachmentsPreviewStripProps {
  attachments: IAttachment[]
  maxPreviews?: number
  className?: string
  onPreview?: (attachments: IAttachment[], index: number) => void
}

const TILE_CLASS =
  'tw:w-20 tw:h-14 tw:rounded tw:overflow-hidden tw:bg-md3-surface-container-high tw:border tw:border-solid tw:border-md3-outline-variant tw:shrink-0 tw:flex tw:items-center tw:justify-center'

function PreviewTile({ attachment }: { attachment: IAttachment }) {
  const [imageFailed, setImageFailed] = React.useState(false)
  const kind = getFileKind(attachment.fileName)

  if (kind === 'image' && !imageFailed) {
    return (
      <div className={TILE_CLASS}>
        <img
          src={attachment.url}
          alt={attachment.fileName}
          className="tw:w-full tw:h-full tw:object-cover"
          onError={() => setImageFailed(true)}
        />
      </div>
    )
  }

  if (kind === 'video') {
    return (
      <div className={TILE_CLASS}>
        <video
          src={attachment.url}
          className="tw:w-full tw:h-full tw:object-cover"
          muted
          preload="metadata"
        />
      </div>
    )
  }

  return (
    <div className={TILE_CLASS}>
      {kind === 'image' && imageFailed ? (
        <PhotoIcon size="md" className="tw:text-md3-on-surface-variant" />
      ) : (
        <FileTypeIcon kind={kind} />
      )}
    </div>
  )
}

export default function AttachmentsPreviewStrip({
  attachments,
  maxPreviews = 3,
  className,
  onPreview,
}: AttachmentsPreviewStripProps) {
  if (!attachments?.length) return null

  const visible = attachments.slice(0, maxPreviews)
  const overflow = attachments.length - visible.length

  return (
    <div
      className={classList({
        'tw:flex tw:flex-nowrap tw:gap-2 tw:items-center': true,
        [className || '']: !!className,
      })}
    >
      {visible.map((attachment, index) => (
        <div
          key={`${attachment.url}-${index}`}
          role={onPreview ? 'button' : undefined}
          tabIndex={onPreview ? 0 : undefined}
          className={onPreview ? 'tw:cursor-pointer' : undefined}
          onClick={
            onPreview
              ? e => {
                  e.stopPropagation()
                  onPreview(attachments, index)
                }
              : undefined
          }
          onKeyDown={
            onPreview
              ? e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    e.stopPropagation()
                    onPreview(attachments, index)
                  }
                }
              : undefined
          }
        >
          <PreviewTile attachment={attachment} />
        </div>
      ))}
      {overflow > 0 && (
        <div className="tw:w-10 tw:h-14 tw:rounded tw:bg-md3-surface-container-high tw:border tw:border-solid tw:border-md3-outline-variant tw:shrink-0 tw:flex tw:items-center tw:justify-center tw:text-md3-on-surface tw:font-semibold">
          +{overflow}
        </div>
      )}
    </div>
  )
}
